import { useState, useEffect, useRef } from "react";
import AppIcon from "@/components/AppIcon";
import { useNavigate } from "react-router-dom";
import { Hammer, Plus, ImagePlus, Loader2, ShoppingCart, Info, X, Camera, Trash2, Film, Image as ImageIcon, Volume2, VolumeX, Video, Scissors, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { videoPoster, placeholderPoster, uploadStepClip, STEP_CLIP_MAX, STEP_CLIP_MAX_MB } from "@/lib/stepClip";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import DIYExplainerModal from "@/components/DIYExplainerModal";
import VideoTipsModal from "@/components/VideoTipsModal";
import VideoTrimModal from "@/components/VideoTrimModal";
import CameraClipModal from "@/components/CameraClipModal";
import CaptionStylePicker from "@/components/CaptionStylePicker";
import CameraCaptureModal from "@/components/CameraCaptureModal";
import CreatorModeCards from "@/components/CreatorModeCards";
import OtherModeLinks from "@/components/OtherModeLinks";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NarrationMeter, isOverNarration } from "@/components/NarrationMeter";
import DiyStepRecorder, { CountdownBadge } from "@/components/DiyStepRecorder";

const MAX_STEPS = 15;
const MAX_PRODUCTS = 8;
const CATEGORIES = ["Lifestyle", "Education (Kids)", "Education (Teens)", "Education (Adults)", "Teachers", "Family", "Food", "Crypto", "Finance", "Government", "Current News", "Philosophy", "Religion", "History", "Tech", "Home", "Gardening", "People", "Fashion", "Animals", "Sports", "Games", "Travel", "Jobs", "Music", "Automotive", "Video Games", "Entertainment", "Medical", "Health", "Self Help", "Fitness", "Collectibles", "Hobbies", "Weather", "Art"];

// Resize an uploaded image down so files stay small (~under 5MB) before base64 upload.
function resizeImage(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (Math.max(width, height) > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const emptyStep = () => ({ text: "", image: "", motion: "none", kind: "video" });
const fmtLen = (t) => `${Math.floor(t / 60)}:${String(Math.round(t % 60)).padStart(2, "0")}`;
// Rough per-step length: clip slice, spoken words (~2.6 words/s), or a ~5s photo hold.
const estStepSecs = (s) => {
  const words = (s.text || "").trim().split(/\s+/).filter(Boolean).length;
  const clip = s.kind === "video" && s.clipFile ? Math.min(10, Math.max(0, (s.clipDur || 10) - (s.trimStart || 0))) : 0;
  return Math.max(clip, Math.min(16, words / 2.6), 5);
};

// Plays a step's clip (muted, from its trim point) inside the card while the voice-over records.
function ClipPlayback({ file, start = 0, playing }) {
  const ref = useRef(null);
  const [url, setUrl] = useState(null);
  useEffect(() => { if (!file) return undefined; const u = URL.createObjectURL(file); setUrl(u); return () => URL.revokeObjectURL(u); }, [file]);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (playing) { v.currentTime = start; v.play().catch(() => {}); } else { v.pause(); }
  }, [playing, start]);
  if (!url || !playing) return null;
  return <video ref={ref} src={url} muted playsInline autoPlay className="absolute inset-0 w-full h-full object-cover rounded-xl pointer-events-none" data-testid="diy-clip-playback"
    onLoadedMetadata={(e) => { e.currentTarget.currentTime = start; e.currentTarget.play().catch(() => {}); }}
    onTimeUpdate={(e) => { if (e.currentTarget.currentTime >= start + 10) e.currentTarget.pause(); }} />;
}
const emptyProduct = () => ({ name: "", url: "", price: "", description: "" });

export default function DIY() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [available, setAvailable] = useState(null); // null = loading
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Lifestyle");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState(() => Array.from({ length: MAX_STEPS }, emptyStep));
  const [orientation, setOrientation] = useState("landscape"); // 'landscape' | 'portrait' — applies to all step photos
  const [products, setProducts] = useState([emptyProduct()]);
  const [showProducts, setShowProducts] = useState(false);
  const [showExplainer, setShowExplainer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState("");
  const fileRefs = useRef({});
  const videoRefs = useRef({});

  useEffect(() => {
    api.get("/config").then(({ data }) => setAvailable(data.diy_available !== false)).catch(() => setAvailable(true));
  }, []);

  const setStep = (i, patch) => setSteps((arr) => arr.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const removeStep = (i) => setSteps((arr) => (arr.length <= 1 ? arr : arr.filter((_, idx) => idx !== i)));

  const onPickImage = async (i, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    try {
      const data = await resizeImage(file);
      setStep(i, { image: data });
    } catch {
      toast.error("Couldn't read that image — try another");
    }
  };
  const [camIndex, setCamIndex] = useState(null);
  const [showVideoTips, setShowVideoTips] = useState(false);
  const [trimIndex, setTrimIndex] = useState(null);
  const [recIndex, setRecIndex] = useState(null);
  const [countdowns, setCountdowns] = useState({});
  const [nudge, setNudge] = useState(false);
  const [ccStyle, setCcStyle] = useState({ style: "classic", size: "medium", pos: "lower" });
  const filled = steps.filter((s) => s.text.trim() || s.image);
  const estSecs = filled.length ? filled.reduce((a, s) => a + estStepSecs(s), 0) + 7 : 0;
  const [vidCamIndex, setVidCamIndex] = useState(null);
  const videoSlots = steps.filter((s) => s.kind === "video").length;

  const setKind = (i, kind) => {
    if (kind === "video" && steps[i].kind !== "video" && videoSlots >= STEP_CLIP_MAX) {
      toast.error(`All ${STEP_CLIP_MAX} video slots are used — the rest are photo steps`); return;
    }
    setStep(i, { kind, image: "", clipFile: null, clipDur: 0, keepSound: false, motion: "none" });

  };

  const onPickVideo = async (i, file, knownSecs = 0) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file"); return; }
    if (file.size > STEP_CLIP_MAX_MB * 1024 * 1024) {
      toast.error(`This clip is ${(file.size / 1048576).toFixed(1)}MB — the limit is ${STEP_CLIP_MAX_MB}MB`, {
        description: "Shoot at 720p or 1080p · 30fps (not 4K) — we shrink it automatically after upload.",
        action: { label: "How?", onClick: () => setShowVideoTips(true) },
        duration: 9000,
      });
      return;
    }
    try {
      const { poster, duration: probed } = await videoPoster(file);
      const duration = probed || knownSecs;
      if (duration > 20.5) { toast.error(`This clip is ${Math.round(duration)}s — clips can be up to 20 seconds`, { description: "Trim it on your phone first (Photos → Edit), then pick your 10 seconds here." }); return; }
      setStep(i, { image: poster, clipFile: file, clipDur: duration, serverPoster: false, trimStart: 0, keepSound: !steps[i]?.vo });
      if (duration > 10.5) setTrimIndex(i);
    } catch {
      // This browser can't preview the clip (e.g. HEVC on desktop) — accept it; the server grabs the frame.
      setStep(i, { image: placeholderPoster(), clipFile: file, clipDur: knownSecs, serverPoster: true, trimStart: 0, keepSound: !steps[i]?.vo });
      toast.message("Clip added — its preview appears after upload");
    }
  };

  const setProduct = (i, patch) => setProducts((arr) => arr.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const addProduct = () => { if (products.length >= MAX_PRODUCTS) { toast.error(`Up to ${MAX_PRODUCTS} products`); return; } setProducts((a) => [...a, emptyProduct()]); };
  const removeProduct = (i) => setProducts((a) => a.filter((_, idx) => idx !== i));

  const addOneMore = () => {
    setNudge(false);
    const k = steps.findIndex((s) => !s.text.trim() && !s.image);
    const el = document.querySelector(`[data-testid="diy-step-text-${k}"]`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); setTimeout(() => el.focus(), 400); }
  };

  const submit = async (skipNudge = false) => {
    if (title.trim().length < 2) { toast.error("Give your manual a title"); return; }
    const activeSteps = steps.filter((s) => s.text.trim() || s.image);
    if (activeSteps.length === 0) { toast.error("Add at least one step"); return; }
    if (!skipNudge && activeSteps.length < 3) { setNudge(true); return; }
    for (let i = 0; i < activeSteps.length; i++) {
      if (!activeSteps[i].text.trim()) { toast.error(`Step ${i + 1} needs a description`); return; }
      if (activeSteps[i].text.trim().length > 5000) { toast.error(`Step ${i + 1} is very long — please trim it a little (5000 characters max)`); return; }
      if (!activeSteps[i].image) { toast.error(`Step ${i + 1} needs ${activeSteps[i].kind === "video" ? "a video clip" : "a photo"}`); return; }
    }
    const cleanProducts = products
      .filter((p) => p.name.trim() || p.url.trim())
      .map((p) => ({ name: p.name.trim(), url: p.url.trim(), price: p.price.trim(), description: p.description.trim() }));

    setBusy(true);
    try {
      const { data } = await api.post("/manuals/diy", {
        title: title.trim(),
        category,
        description: description.trim(),
        steps: activeSteps.map((s) => ({ text: s.text.trim(), image: s.image, motion: s.motion || "none", captions: s.captions !== false })),
        products: cleanProducts,
        cc_style: ccStyle,
      });
      if (activeSteps.some((s) => isOverNarration(s.text))) {
        toast.message("Some steps are long", {
          description: "Only the first ~16s of each step is narrated — the full text stays in your PDF guide.",
          duration: 6000,
        });
      }
      const clipSteps = activeSteps.map((s, idx) => ({ s, idx })).filter(({ s }) => s.kind === "video" && s.clipFile);
      let clipFails = 0, rawBytes = 0, outBytes = 0, done = 0;
      const prog = {};
      const totalBytes = clipSteps.reduce((a, { s }) => a + s.clipFile.size, 0) || 1;
      const report = () => {
        const sent = clipSteps.reduce((a, { s, idx }) => a + (prog[idx] || 0) * s.clipFile.size, 0);
        setBusyMsg(`Uploading & shrinking video clips · ${done}/${clipSteps.length} ready · ${Math.round((sent / totalBytes) * 100)}%`);
      };
      const queue = [...clipSteps];
      const worker = async () => {
        while (queue.length) {
          const { s, idx } = queue.shift();
          try {
            const clip = await uploadStepClip(data.id, idx, s.clipFile, s.keepSound, (f) => { prog[idx] = f; report(); }, s.serverPoster, s.trimStart);
            rawBytes += clip.raw_size || s.clipFile.size; outBytes += clip.size || 0;
          } catch (e) {
            clipFails += 1;
            toast.error(e?.response?.data?.detail || `Clip for step ${idx + 1} didn't upload — its first frame is used as a photo`);
          }
          done += 1; report();
        }
      };
      if (clipSteps.length) { report(); await Promise.all([worker(), worker()]); }
      setBusyMsg("");
      const mb = (b) => (b / 1048576).toFixed(1);
      if (clipSteps.length - clipFails > 0) toast.success(`${clipSteps.length - clipFails} video ${clipSteps.length - clipFails === 1 ? "clip" : "clips"} added · shrunk ${mb(rawBytes)}MB → ${mb(outBytes)}MB`, { duration: 7000 });
      toast.success("Your manual is ready — let's make the video 🎬");
      // Attach any per-step voice-over takes recorded on the builder (server index follows activeSteps order).
      const voUploads = activeSteps
        .map((s, idx) => (s.vo ? api.post(`/manuals/${data.id}/steps/${idx}/voiceover`, { audio: s.vo }).catch(() => null) : null))
        .filter(Boolean);
      if (voUploads.length) {
        await Promise.all(voUploads);
        toast.success(`Attached ${voUploads.length} voice-over ${voUploads.length === 1 ? "recording" : "recordings"} — pick "My Voice" in the studio to hear them`);
      }
      navigate(`/manual/${data.id}/complete`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build your manual — please try again");
      setBusy(false);
    }
  };

  if (available === null) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 size={28} className="spin text-[#5d8a52]" /></div>;
  }
  if (available === false) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="diy-unavailable">
        <Hammer size={40} className="mx-auto text-[#b4c8ad] mb-4" />
        <h1 className="text-2xl font-extrabold mb-2">Video Creator Mode is taking a break</h1>
        <p className="text-[#6b6880]">This pathway isn't available right now. Check back soon!</p>
      </div>
    );
  }

  if (busy) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 flex flex-col items-center text-center sms-fade-up">
        <AppIcon size={52} />
        <h1 className="text-2xl md:text-3xl font-extrabold mt-4 mb-1">Building your manual…</h1>
        <p className="text-[#6b6880]" data-testid="diy-busy-msg">{busyMsg || "Saving your steps and photos. This only takes a moment."}</p>
        <Loader2 size={26} className="spin text-[#5d8a52] mt-6" />
      </div>
    );
  }

  return (
    <div className="diy-dark" style={{ background: "#000", minHeight: "calc(100vh - 64px)" }} data-testid="diy-page">
      <style>{`
        .diy-dark .sms-card{ background-color:#0f0f16 !important; border:2px solid #5d8a52 !important; color:#fff !important; box-shadow:none !important; }
        .diy-dark label{ color:#fff !important; }
        .diy-dark .sms-input{ background:#000 !important; color:#fff !important; border:2px solid #5d8a52 !important; }
        .diy-dark .sms-input::placeholder{ color:rgba(255,255,255,.45) !important; }
        .diy-dark select.sms-input option{ background:#0f0f16; color:#fff; }
      `}</style>
      <div className="max-w-2xl mx-auto px-4 pt-10 pb-24">
      <div className="flex flex-col items-center mb-6 sms-fade-up">
        <Logo size="lg" tagline taglineText="Your Pics, Your Voice, Your Story" taglineClassName="text-2xl md:text-3xl font-extrabold tracking-wide" />
        <button
          type="button"
          data-testid="diy-learn-more-btn"
          onClick={() => setShowExplainer(true)}
          className="mt-5 inline-flex items-center gap-2 text-base font-bold text-white px-4 py-2 rounded-full transition-transform hover:-translate-y-0.5"
          style={{ background: "#1e3aaa", boxShadow: "0 8px 18px -6px rgba(30,58,170,.55)" }}
        >
          <Hammer size={17} /> Upload your story — Smart-Edit does the rest! Tap to learn more
        </button>
        <OtherModeLinks />
      </div>

      <CreatorModeCards active="diy" />

      {/* Title */}
      <div className="sms-card sms-tweed p-4 mb-4 sms-fade-up" style={{ animationDelay: ".03s" }}>
        <label className="block font-bold mb-1.5">Title</label>
        <input
          data-testid="diy-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="e.g. How to build a simple bird feeder"
          className="sms-input w-full px-4 py-4 text-lg"
          style={{ border: "2px solid #1e3aaa" }}
        />
        <p className="text-sm text-[#9a96ac] mt-2 px-1">Add a clean and concise title for your video here, you can edit it later.</p>

        <div className="mt-5">
          <label className="block font-semibold text-sm mb-1.5 text-[#4a475c]">Category</label>
          <select
            data-testid="diy-category-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="sms-input w-full px-4 py-4 text-lg bg-white"
            style={{ border: "2px solid #1e3aaa" }}
          >
            {[...CATEGORIES].sort((a, b) => a.localeCompare(b)).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-1.5">
            <label className="font-semibold text-sm text-[#4a475c]">Video Description</label>
            <span className="tabular-nums text-xs text-[#9a96ac]" data-testid="diy-description-counter">{description.length}/300</span>
          </div>
          <textarea
            data-testid="diy-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 300))}
            maxLength={300}
            rows={4}
            placeholder="Enter a few sentences about your topic and what the viewer should expect to see and learn."
            className="sms-input w-full px-4 py-3 text-lg resize-none leading-relaxed"
            style={{ border: "2px solid #1e3aaa" }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2 px-1">
          <h2 className="font-extrabold text-lg text-white sms-outline-dark">Your steps</h2>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold transition-colors ${filled.length >= 3 ? "bg-[#5d8a52] text-white" : "bg-black/60 text-[#f0c975] border border-[#f0c975]/50"}`} data-testid="diy-length-meter">
            <Clock size={12} /> Your video: ~{fmtLen(estSecs)} · {filled.length} {filled.length === 1 ? "step" : "steps"}
          </span>
        </div>
        <p className="text-sm text-white mb-3 px-1 flex items-start gap-1.5">
          <Info size={14} className="text-white mt-0.5 shrink-0" />
          <span>Every step needs a photo or a video clip — flip the Video / Image switch on any card. Mix them, or go all video. Use at least 3 steps (a 30-second video) and up to 15 — every step you add is about 10 more seconds of video.<br />Add subtle image movement to photos via the pulldown menu.</span>
        </p>

        {/* Portrait now lives exclusively in Minute Mode — DIY is landscape-only. */}
        <CaptionStylePicker value={ccStyle} onChange={setCcStyle} />

        <div className="space-y-3">
          {steps.map((s, i) => (
            <div key={i}>
            <div className="flex justify-center mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1e3aaa] text-white text-xs font-extrabold shadow" data-testid={`diy-step-badge-${i}`}>{i + 1}</span>
            </div>
            <div className="sms-card sms-tweed p-3 sms-fade-up" style={{ border: "2px solid #5d8a52" }} data-testid={`diy-step-${i}`}>
              <div className="flex items-start gap-3">
                {/* Left column: thumbnail, then the camera-move pulldown + step number / trashcan beneath it */}
                <div className={`shrink-0 ${orientation === "portrait" ? "w-28" : "w-40 sm:w-52"}`}>
                  <div className="relative">
                    <button
                      type="button"
                      data-testid={`diy-step-photo-btn-${i}`}
                      onClick={() => (s.kind === "video" ? videoRefs.current[i] : fileRefs.current[i])?.click()}
                      className={`w-full ${orientation === "portrait" ? "aspect-[3/4]" : "aspect-video"} rounded-xl border-2 border-dashed border-[#5d8a52] bg-black flex flex-col items-center justify-center overflow-hidden hover:border-[#a9d79b] transition-all`}
                    >
                      {s.image ? (
                        <img src={s.image} alt={`step ${i + 1}`} className="w-full h-full object-cover" />
                      ) : s.kind === "video" ? (
                        <><Film size={26} className="text-[#f0c975]" /><span className="text-[11px] font-bold text-[#f0c975] mt-1">Add Video</span></>
                      ) : (
                        <><ImagePlus size={26} className="text-[#a9d79b]" /><span className="text-[11px] font-bold text-[#a9d79b] mt-1">Add photo</span></>
                      )}
                    </button>
                    {s.kind === "video" && s.clipFile && <ClipPlayback file={s.clipFile} start={s.trimStart || 0} playing={recIndex === i} />}
                    {s.kind === "video" && s.clipFile && (
                      <span className="absolute top-1 left-1 inline-flex items-center gap-1 rounded-full bg-black/75 px-2 py-0.5 text-[10px] font-extrabold text-[#f0c975]" data-testid={`diy-step-video-badge-${i}`}>
                        <Film size={11} /> {s.clipDur ? `${Math.min(10, Math.round(s.clipDur))}s` : "≤10s"}
                      </span>
                    )}
                    {s.kind === "video" && s.clipFile && s.clipDur > 10.5 && !s.serverPoster && (
                      <span role="button" onClick={(e) => { e.stopPropagation(); setTrimIndex(i); }} data-testid={`diy-step-trim-${i}`}
                        className="absolute top-1 right-1 inline-flex items-center gap-1 rounded-full bg-[#f0c975] px-2 py-0.5 text-[10px] font-extrabold text-black hover:bg-white transition-colors">
                        <Scissors size={11} /> Trim
                      </span>
                    )}
                    {s.kind === "video" ? <span
                      role="button"
                      data-testid={`diy-step-record-clip-${i}`}
                      onClick={(e) => { e.stopPropagation(); setVidCamIndex(i); }}
                      title="Record a 10-second clip with your camera"
                      className="absolute bottom-1 left-1 w-8 h-8 rounded-full bg-[#dc2626] border-2 border-white flex items-center justify-center text-white shadow-md hover:bg-[#b91c1c] active:scale-95 transition-[background-color,transform]"
                    >
                      <Video size={14} />
                    </span> : <span
                      role="button"
                      data-testid={`diy-step-camera-${i}`}
                      onClick={(e) => { e.stopPropagation(); setCamIndex(i); }}
                      title="Take a photo with your camera"
                      className="absolute bottom-1 left-1 w-7 h-7 rounded-full bg-black/70 border border-[#5d8a52] flex items-center justify-center text-[#a9d79b] hover:text-white hover:border-[#a9d79b] transition-colors"
                    >
                      <Camera size={14} />
                    </span>}
                  </div>
                  <input
                    ref={(el) => (fileRefs.current[i] = el)}
                    data-testid={`diy-step-file-${i}`}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onPickImage(i, e.target.files?.[0])}
                  />
                  <input
                    ref={(el) => (videoRefs.current[i] = el)}
                    data-testid={`diy-step-video-file-${i}`}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => { onPickVideo(i, e.target.files?.[0]); e.target.value = ""; }}
                  />
                  {/* Camera-move pulldown — full column width so it reads clearly, including on mobile */}
                  <p className={`mt-2 -mb-1.5 text-[10px] font-extrabold uppercase tracking-wide ${s.kind === "video" ? "text-white/30" : "text-[#a9d79b]"}`} data-testid={`diy-step-motion-label-${i}`}>Still Image Move</p>
                  <select
                    data-testid={`diy-step-motion-${i}`}
                    value={s.motion || "none"}
                    disabled={s.kind === "video"}
                    onChange={(e) => setStep(i, { motion: e.target.value })}
                    title="Camera movement on this photo (text stays pinned)"
                    className="sms-input w-full mt-2 px-2 py-1.5 text-xs font-bold text-white bg-black cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <option value="none">None</option>
                    <option value="push_in">Push In</option>
                    <option value="reveal">Reveal</option>
                    <option value="dolly_left">Dolly L</option>
                    <option value="dolly_right">Dolly R</option>
                    <option value="auto">Auto ✨</option>
                  </select>
                  <div className={`mt-2 grid grid-cols-2 rounded-full border-2 overflow-hidden text-[11px] font-extrabold transition-colors ${s.kind === "video" ? "border-[#f0c975]" : "border-[#5d8a52]"}`} data-testid={`diy-step-kind-${i}`}>
                    {[["video", "Video", Film], ["image", "Image", ImageIcon]].map(([k, label, Icon]) => (
                      <button key={k} type="button" data-testid={`diy-step-kind-${k}-${i}`} onClick={() => s.kind !== k && setKind(i, k)}
                        disabled={k === "video" && s.kind !== "video" && videoSlots >= STEP_CLIP_MAX}
                        className={`py-1 inline-flex items-center justify-center gap-1 transition-colors disabled:opacity-35 focus:outline-none ${s.kind === k ? (k === "video" ? "bg-[#f0c975] text-black" : "bg-[#5d8a52] text-white") : "text-white/70 hover:bg-white/10"}`}>
                        <Icon size={12} /> {label}
                      </button>
                    ))}
                  </div>
                  {s.kind === "video" && (
                    <button type="button" data-testid={`diy-step-video-tips-${i}`} onClick={() => setShowVideoTips(true)} className="mt-1.5 w-full text-[10px] font-bold text-[#f0c975] underline underline-offset-2 hover:text-white transition-colors">
                      Video tips · {STEP_CLIP_MAX_MB}MB max
                    </button>
                  )}
                  {((s.kind === "video" && s.clipFile && s.keepSound) || s.vo) && (
                    <button type="button" data-testid={`diy-step-cc-${i}`} onClick={() => setStep(i, { captions: s.captions === false })}
                      title="Turn your spoken words into on-screen captions automatically"
                      className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-extrabold border-2 transition-colors ${s.captions !== false ? "border-white bg-white text-black" : "border-white/20 text-white/60"}`}>
                      <span className={`px-1 rounded-[3px] text-[9px] leading-tight border ${s.captions !== false ? "border-black" : "border-current"}`}>CC</span> Auto Captions {s.captions !== false ? "on" : "off"}
                    </button>
                  )}
                  {s.kind === "video" && s.clipFile && (
                    <button type="button" data-testid={`diy-step-keep-sound-${i}`} onClick={() => setStep(i, { keepSound: !s.keepSound })}
                      className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-extrabold border-2 transition-colors ${s.keepSound ? "border-[#f0c975] text-[#f0c975]" : "border-white/20 text-white/60"}`}>
                      {s.keepSound ? <Volume2 size={12} /> : <VolumeX size={12} />} {s.keepSound ? "Clip sound on" : "Clip sound off"}
                    </button>
                  )}
                </div>
                {/* Right column: voice deck (step number + trashcan now flank the Record button), counter above the text field */}
                <div className="flex-1 min-w-0 flex flex-col">
                  {/* Voice deck — record your own, or let AI Male/Female read it (else the step is silent) */}
                  <DiyStepRecorder index={i} onCountdown={(n) => setCountdowns((c) => (c[i] === n ? c : { ...c, [i]: n }))} maxSecs={s.kind === "video" && s.clipFile ? 10 : undefined} onRecordingChange={(on) => setRecIndex((cur) => (on ? i : cur === i ? null : cur))} canRemove={steps.length > 1} onRemove={() => removeStep(i)} take={s.vo} voice={s.voice || "none"} onTake={(d) => setStep(i, d ? { vo: d, keepSound: false } : { vo: d })} onVoice={(v) => setStep(i, { voice: v })} />
                  {/* Word counter — dropped to the right, just above the text field */}
                  <div className="mt-auto pt-2 flex items-end gap-2">
                    <NarrationMeter text={s.text} className="flex-1 min-w-0" />
                    {steps.length > 1 && (
                      <button type="button" data-testid={`diy-step-remove-m-${i}`} onClick={() => removeStep(i)} title="Remove this step" className="sm:hidden shrink-0 text-[#c0392b] hover:text-[#96271a] p-1 transition-colors">
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {countdowns[i] > 0 && (
                <div className="flex mt-2"><div className="flex-1" /><CountdownBadge count={countdowns[i]} testid={`diy-vo-countdown-${i}`} className="flex-1" /></div>
              )}
              {/* Step text — full card width at the bottom, lifted up to keep the card compact */}
              <textarea
                data-testid={`diy-step-text-${i}`}
                value={s.text}
                onChange={(e) => setStep(i, { text: e.target.value })}
                maxLength={600}
                placeholder={`Describe step ${i + 1} in your own words…`}
                className="sms-input w-full mt-2 px-3 py-2 text-sm resize-none leading-snug min-h-[108px] sm:min-h-[80px]"
                style={{ border: "2px solid #1e3aaa" }}
              />
            </div>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        data-testid="diy-submit-btn"
        onClick={() => submit()}
        className="w-full inline-flex items-center justify-center gap-2 font-extrabold py-4 rounded-xl text-white text-base transition-colors bg-[#dc2626] hover:bg-[#b91c1c]"
      >
        <Hammer size={18} /> Generate Steps
      </button>

      <DIYExplainerModal open={showExplainer} onClose={() => setShowExplainer(false)} />
      <VideoTipsModal open={showVideoTips} onClose={() => setShowVideoTips(false)} />
      <Dialog open={nudge} onOpenChange={(o) => !o && setNudge(false)}>
        <DialogContent className="bg-[#0f0f16] border-2 border-[#5d8a52] text-white max-w-sm" data-testid="diy-short-nudge">
          <DialogHeader>
            <DialogTitle className="text-[#f0c975] text-lg font-extrabold">Add one more step?</DialogTitle>
            <DialogDescription className="text-white/75">
              You have {filled.length} {filled.length === 1 ? "step" : "steps"} (~{fmtLen(estSecs)}). Videos with 3 steps or more (about 30 seconds) feel fuller and keep viewers watching.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => { setNudge(false); submit(true); }} data-testid="diy-nudge-continue" className="flex-1 rounded-full border-2 border-white/25 py-2.5 font-extrabold hover:bg-white/10 transition-colors">Continue anyway</button>
            <button type="button" onClick={addOneMore} data-testid="diy-nudge-add" className="flex-1 rounded-full bg-[#dc2626] hover:bg-[#b91c1c] py-2.5 font-extrabold transition-colors">Add a step</button>
          </div>
        </DialogContent>
      </Dialog>
      <VideoTrimModal
        file={trimIndex != null ? steps[trimIndex]?.clipFile : null}
        duration={trimIndex != null ? steps[trimIndex]?.clipDur : 0}
        initialStart={trimIndex != null ? steps[trimIndex]?.trimStart || 0 : 0}
        onClose={() => setTrimIndex(null)}
        onConfirm={(start, poster) => { setStep(trimIndex, { trimStart: start, ...(poster ? { image: poster } : {}) }); setTrimIndex(null); }}
      />
      <CameraClipModal open={vidCamIndex != null} onClose={() => setVidCamIndex(null)} onUse={(file, secs) => onPickVideo(vidCamIndex, file, secs)} />
      {camIndex !== null && (
        <CameraCaptureModal
          portrait={orientation === "portrait"}
          onCapture={(dataUrl) => setStep(camIndex, { image: dataUrl })}
          onClose={() => setCamIndex(null)}
        />
      )}
      </div>
    </div>
  );
}
